import { useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { compact } from 'lodash-es';
import { Column, Menu, Popover, TextButton, Typography } from 'seagull';
import { AddCircleLiner24, WideArrowDownward } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useStore, useStoreActions } from "../../../../../../contexts";
import { useSwitchBusiness } from "../../../../../../hooks/useSwitchBusiness";
import { selectors } from "../../../../../../store/state-selectors";
import { useCreateVendorAccount } from "../../CreateAVendorAccount/useCreateVendorAccount";
import { getBusinessTriggerLogoUrl, getBusinessTriggerSubtext } from "../BusinessSwitcher.utils";
import { BusinessSwitcherLogo } from "./BusinessSwitcherLogo";
import styles from "./BusinessSwitcherV2.module.scss";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export const getBusinessSwitcherV2TID = () => getTestIds();
export const BusinessSwitcherV2 = _ref => {
  var _anchorEl$current;
  let {
    userInfo
  } = _ref;
  const {
    BusinessSwitcherV2TID
  } = useMemo(() => getBusinessSwitcherV2TID(), []);
  const {
    logBI,
    resetBusinessData,
    setWorksAt
  } = useStoreActions();
  const {
    switchBusiness
  } = useSwitchBusiness();
  const workspaces = userInfo.workspaces() || [];
  const isClient = useStore(selectors.user.isClient);
  const onCreateVendorAccount = useCreateVendorAccount(() => logBI(18107));
  const [open, setOpen] = useState(false);
  const anchorEl = useRef(null);
  const validWorkspaces = workspaces.filter(workspace => workspace.isCompletelySetupAndDisplayable());
  const hasAVendorAccount = userInfo.hasAVendorAccount();
  const menuItems = useMemo(() => {
    return validWorkspaces.map((workspace, index) => ({
      id: workspace.id(),
      testId: BusinessSwitcherV2TID.MenuItem(index),
      body: workspace.name(),
      subtext: workspace.getBusinessSwitcherItemSubtext(),
      prefixIcon: /*#__PURE__*/_jsx(BusinessSwitcherLogo, {
        testId: BusinessSwitcherV2TID.MenuItem(index),
        src: workspace.getBusinessSwitcherItemLogoUrl()
      }),
      onClick: () => {
        setOpen(false);
        switchBusiness(workspace.id());
      }
    }));
  }, [validWorkspaces, switchBusiness, BusinessSwitcherV2TID]);
  const footerItems = useMemo(() => {
    return compact([hasAVendorAccount && {
      variant: 'contextualAction',
      props: {
        testId: BusinessSwitcherV2TID.ContextualAction(),
        title: 'Create an additional account',
        onClick: () => {
          setOpen(false);
          logBI(11151);
          resetBusinessData();
          setWorksAt();
        },
        icon: /*#__PURE__*/_jsx(AddCircleLiner24, {})
      }
    }, !hasAVendorAccount && {
      variant: 'promotionalAction',
      props: {
        testId: BusinessSwitcherV2TID.PromotionalAction(),
        title: 'Try Anchor for your business',
        body: 'Send proposals, bill, and get paid.\nAs simple as that.',
        onClick: () => {
          setOpen(false);
          onCreateVendorAccount();
        },
        className: {
          wrapper: styles.promotionalActionWrapper,
          button: styles.promotionalActionButton
        }
      }
    }]);
  }, [logBI, hasAVendorAccount, onCreateVendorAccount, BusinessSwitcherV2TID, resetBusinessData, setWorksAt, setOpen]);
  const minWidth = (_anchorEl$current = anchorEl.current) === null || _anchorEl$current === void 0 ? void 0 : _anchorEl$current.offsetWidth;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(TextButton, {
      onClick: () => {
        const wasOpen = open;
        setOpen(prev => !prev);
        if (!wasOpen && isClient) {
          logBI(18106);
        }
      },
      "data-testid": BusinessSwitcherV2TID.Button(),
      prefixIcon: /*#__PURE__*/_jsx(BusinessSwitcherLogo, {
        fullLogo: true,
        testId: BusinessSwitcherV2TID.Button(),
        src: getBusinessTriggerLogoUrl(userInfo.business())
      }),
      suffixIcon: /*#__PURE__*/_jsx("div", {
        className: cn(styles.arrowIcon, {
          [styles.open]: open
        }),
        children: /*#__PURE__*/_jsx(WideArrowDownward, {})
      }),
      skin: "secondary",
      underline: "none",
      ref: anchorEl,
      children: /*#__PURE__*/_jsxs(Column, {
        alignItems: "start",
        children: [/*#__PURE__*/_jsx(Typography, {
          variant: "paragraph1",
          ellipsis: true,
          children: userInfo.businessName()
        }), /*#__PURE__*/_jsx(Typography, {
          variant: "paragraph2",
          skin: "secondary",
          ellipsis: true,
          children: getBusinessTriggerSubtext(userInfo.business())
        })]
      })
    }), /*#__PURE__*/_jsx(Popover, {
      open: open,
      onClose: () => setOpen(false),
      anchorEl: anchorEl.current,
      children: /*#__PURE__*/_jsx(Menu, {
        testId: BusinessSwitcherV2TID.Menu(),
        dimensions: {
          width: 'fit-content',
          minWidth
        },
        classes: {
          menuList: styles.menuList
        },
        items: menuItems,
        footer: {
          items: footerItems
        }
      })
    })]
  });
};